import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import type { AnimationItem } from 'lottie-web';
import { SkyPose } from '../service/sky/sky-coach-state.service';
import {
  pickSkyIdleFlourish,
  skyHoldAnimationUrl,
  skyIdleAudioUrl,
  skyIdleAnimationUrl,
  skyPoseAudioUrl,
  skyPoseAnimationUrl,
  skyPoseImageUrl,
  skyShouldSettleToRest,
} from '../service/sky/sky-assets';

/** Quiet time on the dashboard before an idle flourish. */
const FIRST_IDLE_MIN_MS = 20_000;
const FIRST_IDLE_MAX_MS = 40_000;
const NEXT_IDLE_MIN_MS = 30_000;
const NEXT_IDLE_MAX_MS = 50_000;
/** Rest is mostly an eye blink; replay it several times between flourishes. */
const REST_REPLAY_MIN_MS = 6_000;
const REST_REPLAY_MAX_MS = 10_000;
const POSE_CROSSFADE_MS = 220;

type PlaybackMode = 'situation' | 'hold' | 'idle';
type AudioStatus = 'idle' | 'loading' | 'playing' | 'blocked' | 'error';

@Component({
  selector: 'app-sky-character',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sky-character.component.html',
  styleUrl: './sky-character.component.css',
})
export class SkyCharacterComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) pose!: SkyPose;
  @Input() playSituation = true;
  /**
   * Full-screen launch/result transitions: play the situation clip once,
   * skip the static PNG placeholder and rest/idle follow-ups, then emit.
   */
  @Input() oneShot = false;
  @Output() situationComplete = new EventEmitter<void>();

  @ViewChild('lottieHost', { static: true }) lottieHost!: ElementRef<HTMLElement>;

  protected imageUrl = '';
  protected showAnimation = false;
  protected animationMode: PlaybackMode = 'situation';
  protected audioStatus: AudioStatus = 'idle';

  private animation?: AnimationItem;
  private pendingAnimation?: AnimationItem;
  private audio?: HTMLAudioElement;
  private viewReady = false;
  private activePose?: SkyPose;
  private loadToken = 0;
  private animationFrameRequest?: number;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private restReplayTimer?: ReturnType<typeof setTimeout>;
  private hasPlayedIdle = false;
  private restCycleCount = 0;
  private idleArmed = false;
  private stageTransitionTimers = new Set<ReturnType<typeof setTimeout>>();
  private retiredAnimations = new Set<AnimationItem>();

  constructor(private readonly ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.applyPose(this.pose, this.playSituation);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes['pose'] && !changes['playSituation']) || !this.viewReady) {
      return;
    }
    const poseChange = changes['pose'];
    const poseChanged = !!poseChange
      && (poseChange.firstChange || poseChange.previousValue !== poseChange.currentValue);
    const playbackChanged = !!changes['playSituation']
      && changes['playSituation'].previousValue !== changes['playSituation'].currentValue;
    if (!poseChanged && !playbackChanged) {
      return;
    }
    this.applyPose(this.pose, this.playSituation);
  }

  ngOnDestroy(): void {
    this.loadToken++;
    this.clearIdleTimer();
    this.clearRestReplayTimer();
    this.clearStageTransitionTimers();
    this.pendingAnimation?.destroy();
    this.pendingAnimation = undefined;
    this.destroyAudio();
    this.destroyAnimation();
  }

  protected replayWithSound(event: Event): void {
    event.stopPropagation();
    const pose = this.activePose ?? this.pose;
    this.audioStatus = 'loading';
    this.applyPose(pose, true);
  }

  private applyPose(pose: SkyPose, playSituation: boolean): void {
    this.clearIdleTimer();
    this.clearRestReplayTimer();
    this.idleArmed = false;
    this.hasPlayedIdle = false;
    this.restCycleCount = 0;
    this.activePose = pose;
    this.animationMode = 'situation';
    if (!playSituation) {
      this.loadToken++;
      this.pendingAnimation?.destroy();
      this.pendingAnimation = undefined;
      this.destroyAudio();
      this.destroyAnimation();
      this.imageUrl = skyPoseImageUrl(pose);
      this.showAnimation = false;
      return;
    }
    if (this.oneShot) {
      // Transitions stay blank until frame 0 is ready so PNG and Lottie never fight.
      this.imageUrl = '';
      this.showAnimation = false;
      this.destroyAnimation();
    } else {
      this.imageUrl = skyPoseImageUrl(pose);
    }
    void this.mountAnimation(
      skyPoseAnimationUrl(pose),
      'situation',
      skyPoseAudioUrl(pose),
    );
  }

  private async mountAnimation(
    path: string,
    mode: PlaybackMode,
    audioPath: string | null = null,
  ): Promise<void> {
    const token = ++this.loadToken;
    this.animationMode = mode;
    this.prepareAudio(audioPath);
    if (mode !== 'hold') {
      this.clearRestReplayTimer();
    }
    this.pendingAnimation?.destroy();
    this.pendingAnimation = undefined;

    try {
      const lottie = await import('lottie-web');
      if (token !== this.loadToken) {
        return;
      }

      const host = this.lottieHost.nativeElement;
      const staging = document.createElement('div');
      staging.className = 'sky-character-lottie-stage';
      staging.style.width = '100%';
      staging.style.height = '100%';

      const nextAnimation = lottie.default.loadAnimation({
        container: staging,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path,
      });
      this.pendingAnimation = nextAnimation;

      nextAnimation.addEventListener('DOMLoaded', () => {
        if (token !== this.loadToken) {
          nextAnimation.destroy();
          return;
        }

        this.cancelFramePlayback();
        const previousAnimation = this.animation;
        const previousStage = host.lastElementChild as HTMLElement | null;
        staging.style.position = 'absolute';
        staging.style.inset = '0';
        staging.style.opacity = previousAnimation ? '0' : '1';
        staging.style.transition = `opacity ${POSE_CROSSFADE_MS}ms ease`;
        host.appendChild(staging);
        this.animation = nextAnimation;
        this.pendingAnimation = undefined;
        this.alignAnimationToBottom(staging);
        // Paint frame 0 before revealing so the first visible frame is Lottie.
        nextAnimation.goToAndStop(0, true);
        this.showAnimation = true;
        this.playFrames(token, host);
        if (previousAnimation && previousStage) {
          this.retiredAnimations.add(previousAnimation);
          previousStage.style.transition = `opacity ${POSE_CROSSFADE_MS}ms ease`;
          requestAnimationFrame(() => {
            if (token !== this.loadToken) {
              return;
            }
            previousStage.style.opacity = '0';
            staging.style.opacity = '1';
          });
          const transitionTimer = setTimeout(() => {
            previousAnimation.destroy();
            this.retiredAnimations.delete(previousAnimation);
            previousStage.remove();
            this.stageTransitionTimers.delete(transitionTimer);
          }, POSE_CROSSFADE_MS);
          this.stageTransitionTimers.add(transitionTimer);
        } else {
          host.replaceChildren(staging);
        }
        if (mode === 'hold' && !this.oneShot) {
          this.armIdleOnce(token);
        }
      });

      nextAnimation.addEventListener('data_failed', () => {
        nextAnimation.destroy();
        if (this.pendingAnimation === nextAnimation) {
          this.pendingAnimation = undefined;
        }
        if (token !== this.loadToken) {
          return;
        }
        // Keep the currently displayed Lottie during a failed replacement.
        this.showAnimation = !!this.animation;
        if (mode === 'idle') {
          this.afterIdle(token);
        } else {
          this.armIdleOnce(token);
        }
      });
    } catch {
      if (token === this.loadToken) {
        this.showAnimation = !!this.animation;
        if (mode !== 'idle') {
          this.armIdleOnce(token);
        }
      }
    }
  }

  /**
   * Drive frames directly. lottie-web's global autoplay manager can remain paused
   * after a hidden/destroyed animation; explicit frame rendering is local and stable.
   */
  private playFrames(token: number, host: HTMLElement): void {
    const animation = this.animation;
    if (!animation || token !== this.loadToken) {
      return;
    }

    const durationMs = Math.max(1, animation.getDuration(false) * 1000);
    const totalFrames = Math.max(1, animation.getDuration(true));
    const startedAt = performance.now();
    if (this.animationMode === 'hold') {
      this.restCycleCount++;
      host.dataset['restCycle'] = String(this.restCycleCount);
    }
    animation.goToAndStop(0, true);
    host.dataset['animationFrame'] = '0';
    this.playAudio(token);

    const renderFrame = (now: number) => {
      if (token !== this.loadToken || animation !== this.animation) {
        return;
      }

      const progress = Math.min(1, (now - startedAt) / durationMs);
      const frame = Math.min(totalFrames - 0.01, progress * totalFrames);
      animation.goToAndStop(frame, true);
      host.dataset['animationFrame'] = String(Math.floor(frame));

      if (progress < 1) {
        this.animationFrameRequest = requestAnimationFrame(renderFrame);
      } else {
        this.animationFrameRequest = undefined;
        this.onClipComplete(token);
      }
    };

    this.animationFrameRequest = requestAnimationFrame(renderFrame);
  }

  private onClipComplete(token: number): void {
    if (token !== this.loadToken || !this.activePose) {
      return;
    }

    if (this.animationMode === 'situation') {
      this.ngZone.run(() => this.situationComplete.emit());
      if (this.oneShot) {
        return;
      }
      if (skyShouldSettleToRest(this.activePose)) {
        this.clearIdleTimer();
        this.idleArmed = false;
        void this.mountAnimation(skyHoldAnimationUrl('rest'), 'hold');
      } else {
        this.armIdleOnce(token);
      }
      return;
    }

    if (this.animationMode === 'hold') {
      this.armIdleOnce(token);
      this.scheduleRestReplay(token);
      return;
    }

    this.afterIdle(token);
  }

  /** After an idle flourish, settle on rest (or keep last situation frame) — do not replay the emotion. */
  private afterIdle(token: number): void {
    if (token !== this.loadToken || !this.activePose) {
      return;
    }
    this.idleArmed = false;
    void this.mountAnimation(skyHoldAnimationUrl('rest'), 'hold');
  }

  private armIdleOnce(token: number): void {
    if (this.oneShot || this.idleArmed || token !== this.loadToken || !this.activePose) {
      return;
    }
    this.idleArmed = true;
    this.scheduleIdle(token);
  }

  private scheduleIdle(token: number): void {
    this.clearIdleTimer();
    if (!this.activePose) {
      return;
    }

    const minDelay = this.hasPlayedIdle ? NEXT_IDLE_MIN_MS : FIRST_IDLE_MIN_MS;
    const maxDelay = this.hasPlayedIdle ? NEXT_IDLE_MAX_MS : FIRST_IDLE_MAX_MS;
    const delayMs = minDelay + Math.random() * (maxDelay - minDelay);

    this.idleTimer = setTimeout(() => {
      if (token !== this.loadToken || !this.activePose) {
        return;
      }
      const flourish = pickSkyIdleFlourish(this.activePose);
      if (!flourish) {
        return;
      }
      this.hasPlayedIdle = true;
      this.idleArmed = false;
      void this.mountAnimation(
        skyIdleAnimationUrl(flourish),
        'idle',
        skyIdleAudioUrl(flourish),
      );
    }, delayMs);
  }

  private scheduleRestReplay(token: number): void {
    this.clearRestReplayTimer();
    const delayMs =
      REST_REPLAY_MIN_MS + Math.random() * (REST_REPLAY_MAX_MS - REST_REPLAY_MIN_MS);

    this.restReplayTimer = setTimeout(() => {
      if (
        token !== this.loadToken
        || this.animationMode !== 'hold'
        || !this.animation
      ) {
        return;
      }
      this.playFrames(token, this.lottieHost.nativeElement);
    }, delayMs);
  }

  private alignAnimationToBottom(host: HTMLElement): void {
    const svg = host.querySelector('svg');
    svg?.setAttribute('preserveAspectRatio', 'xMidYMax meet');
  }

  private clearIdleTimer(): void {
    if (this.idleTimer != null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
    }
  }

  private clearRestReplayTimer(): void {
    if (this.restReplayTimer != null) {
      clearTimeout(this.restReplayTimer);
      this.restReplayTimer = undefined;
    }
  }

  private clearStageTransitionTimers(): void {
    this.stageTransitionTimers.forEach(timer => clearTimeout(timer));
    this.stageTransitionTimers.clear();
    this.retiredAnimations.forEach(animation => animation.destroy());
    this.retiredAnimations.clear();
  }

  private prepareAudio(path: string | null): void {
    this.stopAudio();
    if (!path) {
      this.audio?.removeAttribute('src');
      this.audio?.load();
      return;
    }
    const audio = this.audio ?? new Audio();
    this.audio = audio;
    audio.preload = 'auto';
    audio.src = path;
    audio.load();
    this.audioStatus = 'loading';
  }

  private playAudio(token: number): void {
    const audio = this.audio;
    if (!audio?.hasAttribute('src') || token !== this.loadToken) {
      return;
    }
    audio.currentTime = 0;
    const playback = audio.play();
    playback.then(() => {
      if (token === this.loadToken) {
        this.audioStatus = 'playing';
      }
    }).catch((error: DOMException) => {
      if (token !== this.loadToken) {
        return;
      }
      this.audioStatus = error.name === 'NotAllowedError' ? 'blocked' : 'error';
    });
    audio.onended = () => {
      if (token === this.loadToken) {
        this.audioStatus = 'idle';
      }
    };
  }

  private stopAudio(): void {
    if (!this.audio) {
      return;
    }
    this.audio.pause();
    this.audio.currentTime = 0;
    if (this.audioStatus !== 'blocked') {
      this.audioStatus = 'idle';
    }
  }

  private destroyAudio(): void {
    this.stopAudio();
    this.audio?.removeAttribute('src');
    this.audio?.load();
    this.audio = undefined;
  }

  private destroyAnimation(): void {
    this.cancelFramePlayback();
    this.animation?.destroy();
    this.animation = undefined;
    this.lottieHost?.nativeElement.replaceChildren();
  }

  private cancelFramePlayback(): void {
    if (this.animationFrameRequest != null) {
      cancelAnimationFrame(this.animationFrameRequest);
      this.animationFrameRequest = undefined;
    }
  }
}
